import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
} from '@angular/core';
import { IEntity, SafeAny, TableAction, TableRow } from '@pf/shared-common';

@Component({
    selector: 'pf-table-actions',
    templateUrl: './table-actions.component.html',
    styleUrls: ['./table-actions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class TableActionsComponent<T extends IEntity> {
    private readonly cdr = inject(ChangeDetectorRef);
    private _actions: TableAction<T>[] = [];
    private _row: TableRow<T> = null as SafeAny;
    inlineActions: TableAction<T>[] | null = null;
    moreActions: TableAction<T>[] | null = null;

    @Input() set actions(actions: TableAction<T>[] | null) {
        if (!actions || actions.length === 0) {
            return;
        }

        this._actions = (actions || []).map(action => ({ ...action }));
        this.setupActions();
    }

    get actions() {
        return this._actions;
    }

    @Input() index!: number;

    @Input({ required: true }) set row(row: TableRow<T>) {
        this._row = row;
        this.setupActions();
    }

    get row() {
        return this._row;
    }

    actionClicked(action: TableAction<T>) {
        if (action.disable) {
            return;
        }
        action.onClick(this.row, this.index);
    }

    private setupActions() {
        if (!this.actions || !this.row) {
            return;
        }
        this.actions.forEach(action => {
            if (action.onRowChange && this.row) {
                action.onRowChange(this.row, action);
            }
        });
        const displayedActions = this.actions.filter(x => !x.hide);
        const inlineActions = displayedActions.filter(
            action => !!action.inline
        );
        const moreActions = displayedActions.filter(action => !action.inline);
        if (inlineActions.length === 0) {
            this.inlineActions = [displayedActions[0]];
            this.moreActions =
                displayedActions.length > 1 ? displayedActions.slice(1) : [];
        } else {
            this.inlineActions = inlineActions;
            this.moreActions = moreActions;
        }
        this.cdr.markForCheck();
    }
}
